import {useMemo, useState} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {IOption, Select} from "@pg-design/select-module";
import {SlotSlider} from "@pg-design/slot-slider-module";
import {Text} from "@pg-design/text-module";
import {RegionType} from "@pg-rp/common-types";

import {useGetOfferListQuery} from "../../../offer/api/get_offer_list";
import {OfferBoxPlaceholder} from "../../../offer/atoms/OfferBoxPlaceholder";
import {OfferBoxWithOfferDetailsAction} from "../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {IOfferBox} from "../../../offer/components/offer_box/OfferBox";
import {OfferModal} from "../../../offer/components/offer_modal/OfferModal";
import {getOrCreateOfferListingAmplitudeItemListId} from "../../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {OfferType} from "../../../offer/types/OfferType";
import {getOfferCardAddress, IOfferCardAddressContext} from "../../../offer/utils/get_offer_card_address";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {getRequestStateFromRtkQuery} from "../../../store/utils/get_request_state_from_rtk_query";
import {useGetDistrictRegionsByParentQuery} from "../api/get_district_regions_by_parent_api";
import {PricesViewType} from "../helpers/prices_view_type";
import {usePricesCityOffersViewItemListTracking} from "../hooks/use_prices_city_offers_view_item_list_tracking";
import {PRICES_OFFERS_LIST_NAME, PricesCityAnalyticsLocation} from "../tracking/prices_city_analytics";
import {trackPricesCityButtonClick} from "../tracking/track_prices_city_button_click";
import {trackPricesCityOffersFiltersApplied} from "../tracking/track_prices_city_offers_filters_applied";
import {getPricesRegionOffersQuery} from "../utils/get_prices_offers_query";
import {getRegionShortLabel} from "../utils/get_region_short_label";

interface Props {
    region: Pick<IRegionReportPrice, "id">;
    type: PricesViewType;
    showDistrictFilter: boolean;
    location?: PricesCityAnalyticsLocation;
}

// Sentinel for the city-wide option in the district select - a district's own region id is always
// a positive BigData id, so it can never collide with this value.
const ALL_DISTRICTS_OPTION_VALUE = -1;

const OFFERS_PLACEHOLDERS = [0, 1, 2, 3];

// Always shows "street, district" regardless of the selected filter, matching the mockup - offers
// keep their district name even when "Wszystkie dzielnice" (whole city) is selected.
const offerAddressContext: IOfferCardAddressContext = {
    searchedRegion: {type: RegionType.CITY}
};

const tileEvents: IOfferBox["trackingMeta"]["tileEvents"] = {
    imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
    buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
    linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
    logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
};

export const PricesOffers = (props: Props) => {
    const {region, type, showDistrictFilter, location} = props;

    const [selectedRegionId, setSelectedRegionId] = useState<number>(ALL_DISTRICTS_OPTION_VALUE);

    const {data: districts} = useGetDistrictRegionsByParentQuery(region.id, {skip: !showDistrictFilter});
    const districtOptions: IOption[] = (districts ?? []).map((district) => ({label: getRegionShortLabel(district.short_name), value: district.id}));

    const allDistrictsOption: IOption = {label: "Wszystkie dzielnice", value: ALL_DISTRICTS_OPTION_VALUE};
    const selectOptions = [allDistrictsOption, ...districtOptions];
    const selectedOption = selectOptions.find((option) => option.value === selectedRegionId) ?? allDistrictsOption;

    const offerType = type === PricesViewType.HOUSES ? OfferType.HOUSE : OfferType.FLAT;
    const offersRegionId = selectedRegionId === ALL_DISTRICTS_OPTION_VALUE ? region.id : selectedRegionId;

    const {data, isLoading, isFetching, isSuccess, isError, isUninitialized} = useGetOfferListQuery(getPricesRegionOffersQuery(offersRegionId, offerType));

    const offers = data?.results ?? [];
    const itemListId = useMemo(() => getOrCreateOfferListingAmplitudeItemListId({type: offerType, region: offersRegionId}), [offerType, offersRegionId]);
    const offersCarouselRef = usePricesCityOffersViewItemListTracking({offers, itemListId, isFetching, location});
    // select_item has to carry the same list id as view_item_list, otherwise the two events can't be joined.
    // Without a location the carousel isn't tracked, so the tiles keep their default list context.
    const amplitudeItemListContext = useMemo(() => (location ? {itemListId, itemListName: PRICES_OFFERS_LIST_NAME} : null), [location, itemListId]);

    const showPlaceholders = isLoading || (isFetching && offers.length === 0);
    const isEmpty = !showPlaceholders && !isError && offers.length === 0;

    const changeDistrictFilter = (nextRegionId: number) => {
        if (nextRegionId === selectedRegionId) {
            return;
        }

        setSelectedRegionId(nextRegionId);

        if (location) {
            trackPricesCityOffersFiltersApplied({
                district: selectOptions.find((option) => option.value === nextRegionId)?.label ?? "",
                location
            });
        }
    };

    const trackingMeta: IOfferBox["trackingMeta"] = {
        listId: "najlepsze_oferty_w_okolicy",
        listName: PRICES_OFFERS_LIST_NAME,
        source: "additional",
        tileEvents
    };

    return (
        <Container className={containerStyle}>
            <Text variant="headline_3" as="h2" mb={1.5}>
                {PRICES_OFFERS_LIST_NAME}
            </Text>

            <div ref={offersCarouselRef}>
                <SlotSlider
                    listLabel={PRICES_OFFERS_LIST_NAME}
                    prevLabel="Poprzednie oferty"
                    nextLabel="Następne oferty"
                    onNavigate={
                        location
                            ? (direction) => trackPricesCityButtonClick({buttonName: "rp_apartment_prices_nearby_offers_arrow", value: direction, location})
                            : undefined
                    }
                    listClassName={listStyle}
                    itemClassName={itemStyle}
                    header={
                        showDistrictFilter ? (
                            <Select
                                instanceId="prices-offers-district-select"
                                options={selectOptions}
                                value={selectedOption}
                                onChange={(option) => changeDistrictFilter((option as IOption).value as number)}
                                className={selectStyles}
                            />
                        ) : undefined
                    }
                >
                    {isEmpty || isError
                        ? []
                        : showPlaceholders
                          ? OFFERS_PLACEHOLDERS.map((placeholder) => <OfferBoxPlaceholder key={placeholder} />)
                          : offers.map((offer, index) => (
                                <OfferBoxWithOfferDetailsAction
                                    key={offer.id}
                                    offer={offer}
                                    index={index}
                                    displayAddress={getOfferCardAddress(offer, offerAddressContext)}
                                    showUspVariant
                                    trackingMeta={trackingMeta}
                                    amplitudeTileLocation={location}
                                    amplitudeItemListContext={amplitudeItemListContext}
                                />
                            ))}
                </SlotSlider>
            </div>

            {isEmpty && <Text color="var(--colors-gray-700)">Brak ofert spełniających kryteria w wybranej dzielnicy.</Text>}
            {isError && <Text color="var(--colors-gray-700)">Nie udało się wczytać ofert. Spróbuj ponownie za chwilę.</Text>}

            <OfferModal
                modalName="mainOfferModal"
                offersQuery={{
                    data: offers,
                    isInitialized: !isUninitialized,
                    isSuccess,
                    isError,
                    isLoading,
                    count: data?.count ?? 0,
                    page: data?.page ?? 1,
                    pageSize: data?.page_size ?? 20,
                    requestState: getRequestStateFromRtkQuery({isFetching, isSuccess, isError, isLoading})
                }}
            />
        </Container>
    );
};

const containerStyle = css({
    py: 4.5,

    md: {
        py: 8
    }
});

const selectStyles = css({
    md: {
        maxWidth: "25rem !important"
    }
});

const listStyle = css({
    pb: 3
});

// OfferBoxBase enforces min-width: 288px / max-width: 375px on the card itself - every tier below
// must stay within that range, otherwise the card overflows a narrower slot instead of shrinking into it.
const itemStyle = css({
    width: "30rem",

    sm: {
        width: "32rem"
    },

    md: {
        width: "34rem"
    },

    lg: {
        width: "36rem"
    }
});
import {useMemo, useState} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {IOption, Select} from "@pg-design/select-module";
import {SlotSlider} from "@pg-design/slot-slider-module";
import {Text} from "@pg-design/text-module";
import {RegionType} from "@pg-rp/common-types";

import {useGetOfferListQuery} from "../../../offer/api/get_offer_list";
import {OfferBoxPlaceholder} from "../../../offer/atoms/OfferBoxPlaceholder";
import {OfferBoxWithOfferDetailsAction} from "../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {IOfferBox} from "../../../offer/components/offer_box/OfferBox";
import {OfferModal} from "../../../offer/components/offer_modal/OfferModal";
import {getOrCreateOfferListingAmplitudeItemListId} from "../../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {OfferType} from "../../../offer/types/OfferType";
import {getOfferCardAddress, IOfferCardAddressContext} from "../../../offer/utils/get_offer_card_address";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {getRequestStateFromRtkQuery} from "../../../store/utils/get_request_state_from_rtk_query";
import {useGetDistrictRegionsByParentQuery} from "../api/get_district_regions_by_parent_api";
import {PricesViewType} from "../helpers/prices_view_type";
import {usePricesCityOffersViewItemListTracking} from "../hooks/use_prices_city_offers_view_item_list_tracking";
import {PRICES_OFFERS_LIST_NAME, PricesCityAnalyticsLocation} from "../tracking/prices_city_analytics";
import {trackPricesCityButtonClick} from "../tracking/track_prices_city_button_click";
import {trackPricesCityOffersFiltersApplied} from "../tracking/track_prices_city_offers_filters_applied";
import {getPricesRegionOffersQuery} from "../utils/get_prices_offers_query";
import {getRegionShortLabel} from "../utils/get_region_short_label";

interface Props {
    region: Pick<IRegionReportPrice, "id">;
    type: PricesViewType;
    showDistrictFilter: boolean;
    location?: PricesCityAnalyticsLocation;
}

// Sentinel for the city-wide option in the district select - a district's own region id is always
// a positive BigData id, so it can never collide with this value.
const ALL_DISTRICTS_OPTION_VALUE = -1;

const OFFERS_PLACEHOLDERS = [0, 1, 2, 3];

// Always shows "street, district" regardless of the selected filter, matching the mockup - offers
// keep their district name even when "Wszystkie dzielnice" (whole city) is selected.
const offerAddressContext: IOfferCardAddressContext = {
    searchedRegion: {type: RegionType.CITY}
};

const tileEvents: IOfferBox["trackingMeta"]["tileEvents"] = {
    imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
    buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
    linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
    logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
};

export const PricesOffers = (props: Props) => {
    const {region, type, showDistrictFilter, location} = props;

    const [selectedRegionId, setSelectedRegionId] = useState<number>(ALL_DISTRICTS_OPTION_VALUE);

    const {data: districts} = useGetDistrictRegionsByParentQuery(region.id, {skip: !showDistrictFilter});
    const districtOptions: IOption[] = (districts ?? []).map((district) => ({label: getRegionShortLabel(district.short_name), value: district.id}));

    const allDistrictsOption: IOption = {label: "Wszystkie dzielnice", value: ALL_DISTRICTS_OPTION_VALUE};
    const selectOptions = [allDistrictsOption, ...districtOptions];
    const selectedOption = selectOptions.find((option) => option.value === selectedRegionId) ?? allDistrictsOption;

    const offerType = type === PricesViewType.HOUSES ? OfferType.HOUSE : OfferType.FLAT;
    const offersRegionId = selectedRegionId === ALL_DISTRICTS_OPTION_VALUE ? region.id : selectedRegionId;

    const {data, isLoading, isFetching, isSuccess, isError, isUninitialized} = useGetOfferListQuery(getPricesRegionOffersQuery(offersRegionId, offerType));

    const offers = data?.results ?? [];
    const itemListId = useMemo(() => getOrCreateOfferListingAmplitudeItemListId({type: offerType, region: offersRegionId}), [offerType, offersRegionId]);
    const offersCarouselRef = usePricesCityOffersViewItemListTracking({offers, itemListId, isFetching, location});
    // select_item has to carry the same list id as view_item_list, otherwise the two events can't be joined.
    // Without a location the carousel isn't tracked, so the tiles keep their default list context.
    const amplitudeItemListContext = useMemo(() => (location ? {itemListId, itemListName: PRICES_OFFERS_LIST_NAME} : null), [location, itemListId]);

    const showPlaceholders = isLoading || (isFetching && offers.length === 0);
    const isEmpty = !showPlaceholders && !isError && offers.length === 0;

    const changeDistrictFilter = (nextRegionId: number) => {
        if (nextRegionId === selectedRegionId) {
            return;
        }

        setSelectedRegionId(nextRegionId);

        if (location) {
            trackPricesCityOffersFiltersApplied({
                district: selectOptions.find((option) => option.value === nextRegionId)?.label ?? "",
                location
            });
        }
    };

    const trackingMeta: IOfferBox["trackingMeta"] = {
        listId: "najlepsze_oferty_w_okolicy",
        listName: PRICES_OFFERS_LIST_NAME,
        source: "additional",
        tileEvents
    };

    return (
        <Container className={containerStyle}>
            <Text variant="headline_3" as="h2" mb={1.5}>
                {PRICES_OFFERS_LIST_NAME}
            </Text>

            <div ref={offersCarouselRef}>
                <SlotSlider
                    listLabel={PRICES_OFFERS_LIST_NAME}
                    prevLabel="Poprzednie oferty"
                    nextLabel="Następne oferty"
                    onNavigate={
                        location
                            ? (direction) => trackPricesCityButtonClick({buttonName: "rp_apartment_prices_nearby_offers_arrow", value: direction, location})
                            : undefined
                    }
                    listClassName={listStyle}
                    itemClassName={itemStyle}
                    header={
                        showDistrictFilter ? (
                            <Select
                                instanceId="prices-offers-district-select"
                                options={selectOptions}
                                value={selectedOption}
                                onChange={(option) => changeDistrictFilter((option as IOption).value as number)}
                                className={selectStyles}
                            />
                        ) : undefined
                    }
                >
                    {isEmpty || isError
                        ? []
                        : showPlaceholders
                          ? OFFERS_PLACEHOLDERS.map((placeholder) => <OfferBoxPlaceholder key={placeholder} />)
                          : offers.map((offer, index) => (
                                <OfferBoxWithOfferDetailsAction
                                    key={offer.id}
                                    offer={offer}
                                    index={index}
                                    displayAddress={getOfferCardAddress(offer, offerAddressContext)}
                                    showUspVariant
                                    trackingMeta={trackingMeta}
                                    amplitudeTileLocation={location}
                                    amplitudeItemListContext={amplitudeItemListContext}
                                />
                            ))}
                </SlotSlider>
            </div>

            {isEmpty && <Text color="var(--colors-gray-700)">Brak ofert spełniających kryteria w wybranej dzielnicy.</Text>}
            {isError && <Text color="var(--colors-gray-700)">Nie udało się wczytać ofert. Spróbuj ponownie za chwilę.</Text>}

            <OfferModal
                modalName="mainOfferModal"
                offersQuery={{
                    data: offers,
                    isInitialized: !isUninitialized,
                    isSuccess,
                    isError,
                    isLoading,
                    count: data?.count ?? 0,
                    page: data?.page ?? 1,
                    pageSize: data?.page_size ?? 20,
                    requestState: getRequestStateFromRtkQuery({isFetching, isSuccess, isError, isLoading})
                }}
            />
        </Container>
    );
};

const containerStyle = css({
    py: 4.5,

    md: {
        py: 8
    }
});

const selectStyles = css({
    md: {
        maxWidth: "25rem !important"
    }
});

const listStyle = css({
    pb: 3
});

// OfferBoxBase enforces min-width: 288px / max-width: 375px on the card itself - every tier below
// must stay within that range, otherwise the card overflows a narrower slot instead of shrinking into it.
const itemStyle = css({
    width: "30rem",

    sm: {
        width: "32rem"
    },

    md: {
        width: "34rem"
    },

    lg: {
        width: "36rem"
    }
});
